import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TRANSLATIONS, LANGUAGE_ROUTE_PAIRS } from '../constants';

type Language = 'EN' | 'DE';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Reads /de/... or /en/... from the URL; null for every other path (utility
// routes excluded from the migration, and the brief render before
// CatchAllRedirect fires elsewhere).
function languageFromPath(pathname: string): Language | null {
  if (pathname.startsWith('/en/') || pathname === '/en') return 'EN';
  if (pathname.startsWith('/de/') || pathname === '/de') return 'DE';
  return null;
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // On /de/... and /en/... routes the URL is the sole source of truth for
  // language (see below for why). But a handful of utility pages are
  // deliberately unprefixed — /email-confirmed, /confirmed, /bestaetigt,
  // /feedback — and still need a settable language for their own content
  // and for the shared Navbar/Footer chrome (rendered by Layout in App.tsx
  // for all but /feedback). `override` is that fallback, used only when the
  // URL itself doesn't encode a language. Defaults to 'DE' so SSR/prerender
  // (which only ever renders /de/... and /en/... routes) and the first
  // client render agree for every route this default could apply to — none
  // of the override-consuming routes above are prerendered.
  const [override, setOverride] = useState<Language>('DE');

  const urlLanguage = languageFromPath(location.pathname);

  // Language is derived synchronously from the URL wherever the URL encodes
  // one, instead of read from localStorage. This is what actually fixes
  // production error #418 rather than working around it: LanguageProvider
  // renders inside the router on both the server (StaticRouter in
  // entry-server.tsx) and the client (BrowserRouter in main.tsx), so the
  // client's first render sees the exact same location the server rendered
  // against — there is no localStorage-vs-server disagreement left to cause
  // a hydration mismatch, and no mount-effect correction step is needed for
  // any /de/... or /en/... route.
  const language: Language = urlLanguage ?? override;

  const t = TRANSLATIONS[language];

  // On a /de/.../en/... route, navigating to the URL pair (done by callers
  // such as Navbar.tsx's language switcher) is what actually changes
  // `language` — this only updates the unprefixed-route fallback and
  // records an explicit choice so the one-time browser-language redirect
  // below never re-fires or overrides a manual switch.
  const setLanguage = (lang: Language) => {
    setOverride(lang);
    try {
      localStorage.setItem('pstax_lang', lang);
    } catch {
      // localStorage can throw (private browsing, blocked storage) — the
      // language still works via the URL/override, this is only a
      // remembered preference for the auto-redirect below.
    }
  };

  // One-time redirect for a visitor whose browser is set to English, landing
  // on a German page with no prior language preference recorded yet. Runs
  // once per fresh visitor and never again — see the localStorage guard.
  // This is a plain client-side useEffect, so React guarantees it never runs
  // during renderToString on the server or during the build-time prerender —
  // it cannot affect the HTML crawlers or bots see, and there is no
  // server-side Accept-Language redirect anywhere in this app (see
  // LanguageContext's role in the DE/EN URL migration plan: an automatic
  // redirect for Google's own crawler is exactly the anti-pattern Google's
  // hreflang guidance warns against — hreflang tags are what get searchers
  // to the right language version instead).
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem('pstax_lang');
    } catch {
      return;
    }
    if (stored !== null) return;
    if (language === 'EN') return;

    const browserIsEnglish = navigator.language.toLowerCase().startsWith('en');
    if (!browserIsEnglish) {
      try {
        localStorage.setItem('pstax_lang', 'DE');
      } catch {}
      return;
    }

    const pair = LANGUAGE_ROUTE_PAIRS[location.pathname];
    if (!pair) return; // no known EN counterpart for this exact path — don't force a guess

    try {
      localStorage.setItem('pstax_lang', 'EN');
    } catch {}
    navigate({ pathname: pair.EN, search: location.search, hash: location.hash }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
